// PremiumFollowSolution.tsx
import { PlusIcon } from '@heroicons/react/24/outline'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import _ from 'lodash'
import { useRouter } from 'next/router'
import type { CSSProperties } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-modal'
import { cancel_button, popup_padding, save_button } from 'src/config/config'
import { followTypes, PORTFOLIO_VISIBILITY_PILL } from 'src/constants/constants'

import {
  CREATE_NEW_PORTFOLIO_ID,
  CreateNewPortfolioModal,
  PortfolioOptionTitle,
  PortfolioPickerOption,
} from './CreateNewPortfolioModal'

type FollowTypeValue = 'Portfolio' | 'Reviewed' | 'Watching'
/** portfolioId -> followType; empty string = selected but type not chosen yet */
type PortfolioSelectionMap = Record<string, string>

export type FollowSolutionMeta = {
  isLastInBatch?: boolean
  portfolioUpdated?: boolean
  oldPortfolioData?: any
  portfolioSolutionData?: any
  batchSize?: number
  isDeleted?: boolean
  portfolioSolutionSlug?: any
  showDeleteSuccessToast?: boolean
}

interface FollowSolutionModalProps {
  isOpen: boolean
  followType?: string
  onClose: () => void
  followSolution?: (type: FollowTypeValue, portfolioId?: string, meta?: FollowSolutionMeta) => Promise<void>
  isMoving?: boolean
  portfolioPickerOptions?: PortfolioPickerOption[]
  onSubmitSharedPortfolio?: () => void | Promise<void>
  sharedPortfolioId?: string
  solutionId?: string | null
  sharedPortfolioSolutions?: Array<{
    portfolioId?: string | null
    followingType?: string | null
    sharedPortfolio?: string | null
    slug?: string | null
  }>
}

const ModalStyles: { content: CSSProperties; overlay: CSSProperties } = {
  content: {
    top: '48%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    padding: '0px',
    transform: 'translate(-50%, -50%)',
    borderWidth: '0px',
    backgroundColor: 'transparent',
    border: '0px solid transparent',
    maxHeight: '90vh',
    overflow: 'visible',
  },
  overlay: { zIndex: 1300 },
}

const isValidFollowType = (type: string): type is FollowTypeValue => followTypes.some((f) => f.value === type)

export const PremiumFollowSolution: React.FC<FollowSolutionModalProps> = ({
  isOpen,
  followType,
  onClose,
  followSolution = async () => {},
  isMoving = false,
  portfolioPickerOptions = [],
  onSubmitSharedPortfolio,
  sharedPortfolioId = undefined,
  sharedPortfolioSolutions = [],
  // solutionId = null,
}) => {
  const router = useRouter()

  const [localFollowType, setLocalFollowType] = useState(followType || '')
  const [isDisabledAddSolution, setIsDisabledAddSolution] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isAddSolutionDisabled = isDisabledAddSolution || isSubmitting
  const [selectedId, setSelectedId] = useState<string>(CREATE_NEW_PORTFOLIO_ID)
  const [portfolioSelections, setPortfolioSelections] = useState<PortfolioSelectionMap>({})
  const [portfolioUpdated, setPortfolioUpdated] = useState(false)
  const options = useMemo(() => portfolioPickerOptions, [portfolioPickerOptions])
  const [step, setStep] = useState<'pick' | 'shared-details'>('pick')
  const existingSharedPortfolioSelections = useMemo(() => {
    return sharedPortfolioSolutions.reduce<Record<string, string>>((acc, curr) => {
      acc[curr.portfolioId || 'private_portfolio'] = curr.followingType || ''
      return acc
    }, {})
  }, [sharedPortfolioSolutions])
  const pageName = useMemo(() => router.pathname.replace(/^\//, '').split('/')[0], [router.pathname])
  const slug = useMemo(() => {
    const querySlug = router.query.slug
    if (typeof querySlug === 'string' && querySlug.trim() !== '') {
      return querySlug
    }

    const [pathPart] = router.asPath.split('?')
    const segments = pathPart.replace(/^\/+|\/+$/g, '').split('/')
    if (segments[0] === 'my-shared-portfolios' && segments[1]) {
      return segments[1]
    }
    return ''
  }, [router.asPath, router.query.slug])
  const visibilityPill = {
    minWidthPx: 70,
    minHeightPx: 30,
    paddingY: 8,
    paddingX: 14,
    radiusPx: 15,
    fontSizePx: 14,
    privateBg: PORTFOLIO_VISIBILITY_PILL.privateBg,
    privateText: PORTFOLIO_VISIBILITY_PILL.privateText,
    sharedBg: PORTFOLIO_VISIBILITY_PILL.sharedBg,
    sharedText: PORTFOLIO_VISIBILITY_PILL.sharedText,
  } as const
  const portfolioCardRowTitle =
    'poppins-font block min-w-0 whitespace-normal break-words text-[18px] font-semibold leading-[22px] text-topicDescription md:truncate'
  const portfolioCardRowSubtitle = 'karla-font block text-[16px] font-normal leading-[22px] text-topicDescription'

  const getReadySelections = useCallback((): Array<[string, FollowTypeValue]> => {
    return Object.entries(portfolioSelections).filter(
      ([id, type]) => id !== CREATE_NEW_PORTFOLIO_ID && isValidFollowType(type),
    ) as Array<[string, FollowTypeValue]>
  }, [portfolioSelections])

  const hasValidPortfolioSelections = useCallback((): boolean => {
    const entries = Object.entries(portfolioSelections).filter(([id]) => id !== CREATE_NEW_PORTFOLIO_ID)
    return entries.length > 0 && entries.every(([, type]) => isValidFollowType(type))
  }, [portfolioSelections])

  const isPortfolioSelected = (id: string) => id in portfolioSelections

  const getFollowTypeForPortfolio = (id: string) => (isMoving ? localFollowType : portfolioSelections[id] ?? '')

  const togglePortfolioSelection = (id: string) => {
    if (isMoving) {
      setSelectedId(id)
      setLocalFollowType('')
      return
    }
    setPortfolioSelections((prev) => {
      if (id in prev) {
        const next = { ...prev }
        delete next[id]
        return next
      }
      return { ...prev, [id]: '' }
    })
  }

  const setFollowTypeForPortfolio = (portfolioId: string, value: string) => {
    if (isMoving) {
      updateUserFollowType(value)
      return
    }
    setPortfolioSelections((prev) => ({ ...prev, [portfolioId]: value }))
  }

  const handleClose = () => {
    if (step === 'shared-details') {
      setStep('pick')
      return
    }
    onClose?.()
    setLocalFollowType('')
    setPortfolioSelections({})
    setIsDisabledAddSolution(true)
    setIsSubmitting(false)
  }

  const updateUserFollowType = (value: string) => {
    if (isMoving) {
      setIsDisabledAddSolution(
        isMoving ? (followTypes.some((f) => f.value === value && value != followType) ? false : true) : true,
      )
    } else {
      setIsDisabledAddSolution(false)
    }
    setLocalFollowType(value)
  }

  const handleFollowSolution = async () => {
    if (isSubmitting) {
      return
    }

    const requiresPortfolioSelection = options.length > 0

    if (requiresPortfolioSelection) {
      const ready: Array<[string, FollowTypeValue]> = isMoving
        ? selectedId && selectedId !== CREATE_NEW_PORTFOLIO_ID && isValidFollowType(localFollowType)
          ? [[selectedId, localFollowType as FollowTypeValue]]
          : []
        : hasValidPortfolioSelections()
          ? getReadySelections()
          : []

      if (ready.length === 0 || typeof followSolution !== 'function') {
        return
      }

      setIsSubmitting(true)
      try {
        const oldPortfolioData = sharedPortfolioSolutions.reduce<PortfolioSelectionMap>((acc, curr) => {
          acc[curr.portfolioId || 'private_portfolio'] = curr.followingType || ''
          return acc
        }, {})
        const readyKeys = new Set(ready.map(([key]) => key))

        const deletedData = Object.fromEntries(Object.entries(oldPortfolioData).filter(([key]) => !readyKeys.has(key)))

        type PendingFollowOp = {
          type: FollowTypeValue
          portfolioIdArg: string
          meta: Omit<FollowSolutionMeta, 'isLastInBatch' | 'batchSize'>
        }

        const pendingOps: PendingFollowOp[] = []
        let showDeleteSuccessToast = false

        // Entries already in oldPortfolioData with the same follow type are unchanged — exclude from batch total
        const UnmodifiedEntryCount = ready.filter(
          ([id, type]) => Object.hasOwn(oldPortfolioData, id) && oldPortfolioData[id] === type,
        ).length

        for (const [portfolioId, type] of ready) {
          const portfolioIdToUse =
            portfolioId && portfolioId !== '' && portfolioId === 'private_portfolio' ? 'private_portfolio' : portfolioId
          const isAlreadyExists = Object.hasOwn(oldPortfolioData, portfolioIdToUse)
          const isPortfolioTypeUpdated = isAlreadyExists && oldPortfolioData[portfolioIdToUse] !== type

          if (isAlreadyExists && isPortfolioTypeUpdated) {
            const portfolioOption: any = portfolioPickerOptions?.find((sp) => sp.id === portfolioIdToUse)
            showDeleteSuccessToast = true
            pendingOps.push({
              type,
              portfolioIdArg: portfolioIdToUse,
              meta: {
                portfolioUpdated: portfolioUpdated,
                oldPortfolioData,
                portfolioSolutionData: portfolioOption?.shared_portfolio_solutions ?? [],
                isDeleted: false,
              },
            })
          } else if (!isAlreadyExists) {
            showDeleteSuccessToast = true
            pendingOps.push({
              type,
              portfolioIdArg: portfolioIdToUse,
              meta: {
                portfolioUpdated: false,
                oldPortfolioData,
                isDeleted: false,
              },
            })
          }
        }

        for (const deletedKey of Object.keys(deletedData)) {
          const portfolioSolutionData: any = sharedPortfolioSolutions?.find((el) => el.portfolioId == deletedKey)
          pendingOps.push({
            type: 'Portfolio',
            portfolioIdArg: deletedKey,
            meta: {
              portfolioUpdated: false,
              oldPortfolioData: deletedData,
              isDeleted: true,
              portfolioSolutionSlug: portfolioSolutionData ? portfolioSolutionData?.slug : null,
              showDeleteSuccessToast,
            },
          })
        }

        const batchSize = Math.max(0, ready.length - UnmodifiedEntryCount + Object.keys(deletedData).length)
        for (let i = 0; i < pendingOps.length; i++) {
          const op = pendingOps[i]
          await followSolution(op.type, op.portfolioIdArg, {
            ...op.meta,
            isLastInBatch: i === pendingOps.length - 1,
            batchSize: batchSize || pendingOps.length,
          })
        }
        handleClose()
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    if (localFollowType && isValidFollowType(localFollowType) && typeof followSolution === 'function') {
      setIsSubmitting(true)
      try {
        await followSolution(localFollowType, undefined, { isLastInBatch: true })
        handleClose()
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      setSelectedId(sharedPortfolioId || CREATE_NEW_PORTFOLIO_ID)
      const newPortfolioSelections = sharedPortfolioSolutions.reduce<PortfolioSelectionMap>((acc, curr) => {
        acc[curr.portfolioId || 'private_portfolio'] = curr.followingType || ''
        return acc
      }, {})
      setPortfolioSelections(newPortfolioSelections)
      setStep('pick')
      setLocalFollowType(followType || '')
      setIsDisabledAddSolution(true)
      setIsSubmitting(false)
    }
  }, [isOpen, sharedPortfolioId])

  useEffect(() => {
    const oldPortfolioSelections = sharedPortfolioSolutions.reduce<PortfolioSelectionMap>((acc, curr) => {
      acc[curr.portfolioId || 'private_portfolio'] = curr.followingType || ''
      return acc
    }, {})
    if (_.isEqual(portfolioSelections, oldPortfolioSelections)) {
      setPortfolioUpdated(false)
      if (!isMoving) {
        setIsDisabledAddSolution(true)
      }
    } else {
      setPortfolioUpdated(true)
      if (!isMoving) {
        if (hasValidPortfolioSelections()) {
          setIsDisabledAddSolution(false)
        } else {
          setIsDisabledAddSolution(true)
        }
      }
    }
  }, [sharedPortfolioSolutions, portfolioSelections])

  useEffect(() => {
    if (!isMoving) {
      return
    }

    const hasFollowTypeChanged = isValidFollowType(localFollowType) && localFollowType !== followType
    setIsDisabledAddSolution(!hasFollowTypeChanged)
  }, [isMoving, localFollowType, followType])

  useEffect(() => {
    if (sharedPortfolioId) {
      if (isMoving) {
        setSelectedId(sharedPortfolioId)
      } else {
        setPortfolioSelections((prev) => {
          return { ...prev, [sharedPortfolioId]: prev[sharedPortfolioId] ?? '' }
        })
      }
      return
    }

    if (!isOpen || pageName !== 'my-shared-portfolios' || !slug) {
      return
    }

    const matchedPortfolio = options.find((opt) => {
      if (opt.kind !== 'portfolio' || !('slug' in opt)) {
        return false
      }

      return typeof opt.slug === 'string' && opt.slug === slug
    })

    if (matchedPortfolio) {
      if (isMoving) {
        setSelectedId(matchedPortfolio.id)
      } else {
        setPortfolioSelections((prev) => ({ ...prev, [matchedPortfolio.id]: prev[matchedPortfolio.id] ?? '' }))
      }
    }
  }, [sharedPortfolioId, isOpen, pageName, slug, options, isMoving])

  return (
    <>
      {portfolioPickerOptions && portfolioPickerOptions.length > 0 ? (
        <>
          {step === 'pick' && (
            <Modal
              closeTimeoutMS={200}
              isOpen={isOpen}
              onRequestClose={handleClose}
              style={ModalStyles}
              ariaHideApp={false}
            >
              <div className="relative md:w-[620px]">
                <div className="flex flex-row w-full z-10">
                  <div
                    className="ml-auto md:top-[22px] top-[24px] right-[8px] md:right-[-3px] lg:right-0 cursor-pointer text-textdark relative"
                    onClick={handleClose}
                  >
                    <div className="w-4 h-4 m-2 rounded-md bg-white absolute z-20"></div>
                    <IonIcon name="close-circle" className="!h-[30px] !w-[30px] relative z-30" />
                  </div>
                </div>
                <div
                  className={`premium-follow-solution-container mx-auto flex max-h-[80vh] w-[90%] max-w-[600px] flex-col rounded-lg bg-white p-6 font-bold  ${popup_padding} lg:w-[auto]`}
                >
                  <h4 className="text-[24px] font-bold text-darkgray poppins-font leading-[24px] border-b border-mist pb-[16px] mb-4">
                    Select a portfolio
                  </h4>
                  <p className="text-[18px] font-normal  text-darkgray leading-[22px] karla-font mb-6">
                    Add this solution to your portfolio, or to a shared portfolio.
                  </p>
                  <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-1 overscroll-contain [-webkit-overflow-scrolling:touch]">
                    <ul
                      className="mb-[16px] flex w-full flex-col gap-[8px] bg-white pb-2"
                      role="group"
                      aria-label="Portfolio options"
                    >
                      {options.map((opt) => {
                        const isSelected = isPortfolioSelected(opt.id)
                        const isCreate = opt.kind === 'create-new'
                        const followTypeForPortfolio = getFollowTypeForPortfolio(opt.id)
                        const isAlreadyInSharedPortfolio =
                          !isCreate && Object.hasOwn(existingSharedPortfolioSelections, opt.id)
                        const isOptionDisabled = isAlreadyInSharedPortfolio && isSelected

                        return (
                          <li
                            key={`portfolio-options-list-${opt.id}`}
                            className={cn(
                              'w-full list-none rounded-lg',
                              !isCreate && isSelected ? 'border border-[#6d63ff] bg-[#f4f3ff]' : '',
                            )}
                          >
                            <button
                              type="button"
                              role={isCreate ? undefined : 'checkbox'}
                              aria-checked={isCreate ? undefined : isSelected}
                              onClick={() => {
                                if (isOptionDisabled) {
                                  return
                                }
                                if (isCreate) {
                                  setStep('shared-details')
                                  return
                                }
                                togglePortfolioSelection(opt.id)
                              }}
                              className={cn(
                                'flex min-h-[86px] w-full max-w-[512px] flex-row items-center gap-6 rounded-lg border border-solid px-4 py-4 text-left transition-colors',
                                isCreate
                                  ? 'border-[#c5cbd3] bg-[#D6DADF] hover:bg-[#C8CDD4]'
                                  : isSelected
                                    ? 'min-h-0 border-transparent bg-transparent p-0'
                                    : 'border-[#dddddd] bg-white hover:bg-gray-50/80',
                              )}
                            >
                              {isCreate ? (
                                <span
                                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-textdark [&>svg]:block [&>svg]:shrink-0"
                                  aria-hidden
                                >
                                  <PlusIcon className="h-6 w-6 shrink-0 text-[#011D58]" aria-hidden />
                                </span>
                              ) : (
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center" aria-hidden>
                                  <span
                                    className={cn(
                                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border-2',
                                      isOptionDisabled
                                        ? 'border-[rgb(41_41_43_/_26%)] bg-[rgb(41_41_43_/_42%)]'
                                        : isSelected
                                          ? 'border-[#3329ef] bg-[#3329ef]'
                                          : 'border-[#dddddd] bg-white',
                                      isOptionDisabled ? 'cursor-not-allowed' : '',
                                    )}
                                  >
                                    {isSelected ? (
                                      <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none" aria-hidden>
                                        <path
                                          d="M2 6l3 3 5-6"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : null}
                                  </span>
                                </span>
                              )}
                              <span className="min-w-0 flex-1">
                                <span className="mb-[7px] block min-w-0">
                                  <PortfolioOptionTitle title={opt.title} className={portfolioCardRowTitle} />
                                </span>
                                <span className={portfolioCardRowSubtitle}>{opt.subtitle}</span>
                              </span>
                              {opt.kind === 'portfolio' ? (
                                <span
                                  className="box-border inline-flex h-[30px] w-max shrink-0 items-center justify-center whitespace-nowrap font-primary font-medium tracking-normal opacity-100"
                                  style={{
                                    minWidth: visibilityPill.minWidthPx,
                                    minHeight: visibilityPill.minHeightPx,
                                    paddingTop: visibilityPill.paddingY,
                                    paddingRight: visibilityPill.paddingX,
                                    paddingBottom: visibilityPill.paddingY,
                                    paddingLeft: visibilityPill.paddingX,
                                    borderRadius: visibilityPill.radiusPx,
                                    backgroundColor:
                                      opt.visibility === 'private' ? visibilityPill.privateBg : visibilityPill.sharedBg,
                                    color:
                                      opt.visibility === 'private'
                                        ? visibilityPill.privateText
                                        : visibilityPill.sharedText,
                                    fontSize: visibilityPill.fontSizePx,
                                    lineHeight: `${visibilityPill.minHeightPx - 2 * visibilityPill.paddingY}px`,
                                    letterSpacing: 0,
                                  }}
                                >
                                  {opt.visibility === 'private' ? 'Private' : 'Shared'}
                                </span>
                              ) : null}
                            </button>
                            {!isCreate && isSelected ? (
                              <ul
                                className="mt-[16px] mb-[24px] flex w-full max-w-[512px] flex-col gap-[16px] px-[24px]"
                                role="radiogroup"
                                aria-label={`Follow types for ${opt.title}`}
                              >
                                {followTypes.map((followDetail, index) => (
                                  <li
                                    className={cn(
                                      'flex w-full items-center gap-4 rounded-lg border pl-[24px] py-[16px]',
                                      followDetail.value === followTypeForPortfolio
                                        ? 'border-[#3329ef] bg-[#f1f0fe]'
                                        : 'border-[#dddddd] bg-[#f8f8f8]',
                                    )}
                                    key={`follow-type-${opt.id}-${followDetail.label}-${index}`}
                                  >
                                    <div className="h-5 w-5 shrink-0 cursor-pointer">
                                      <input
                                        type="radio"
                                        className="h-full w-full cursor-pointer"
                                        name={`followType-${opt.id}`}
                                        value={followDetail.value}
                                        onChange={(e) => setFollowTypeForPortfolio(opt.id, e.target.value)}
                                        checked={followTypeForPortfolio === followDetail.value}
                                      />
                                    </div>

                                    <div className="flex flex-col">
                                      <span className="poppins-font mb-[5px] text-[18px] font-semibold leading-[100%] text-[#333333]">
                                        {followDetail.label}
                                      </span>
                                      <span className="karla-font text-[16px] font-normal leading-[22px] text-[#333333]">
                                        {followDetail.description}
                                      </span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                  <div className="mt-6 flex w-full min-w-0 flex-row flex-wrap items-center md:gap-[16px] gap-[8px] font-sans">
                    <button
                      type="button"
                      onClick={handleFollowSolution}
                      disabled={isAddSolutionDisabled}
                      className={cn(
                        save_button,
                        isAddSolutionDisabled ? 'bg-[#b3bbcd] cursor-not-allowed' : 'bg-navy cursor-pointer',
                      )}
                    >
                      <p className="text-white">Add Solution</p>
                    </button>

                    <button className={`${cancel_button}`} type="button" onClick={handleClose}>
                      <p className="text-[#333333]">Cancel</p>
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {step === 'shared-details' && (
            <>
              <CreateNewPortfolioModal
                isOpen={true}
                onClose={handleClose}
                onAddSolution={() => {}}
                portfolioOptions={options}
                onSubmitSharedPortfolio={onSubmitSharedPortfolio}
              />
            </>
          )}
        </>
      ) : (
        <>
          <Modal
            closeTimeoutMS={200}
            isOpen={isOpen}
            onRequestClose={handleClose}
            style={ModalStyles}
            ariaHideApp={false}
          >
            <div className="relative md:w-[620px]">
              <div className="flex flex-row w-full z-10">
                <div
                  className="ml-auto top-[24px] right-[6px] md:right-[-2px] lg:right-0 cursor-pointer text-textdark relative"
                  onClick={handleClose}
                >
                  <div className="w-4 h-4 m-2 rounded-md bg-white absolute z-20"></div>
                  <IonIcon name="close-circle" className="!h-[30px] !w-[30px] relative z-30" />
                </div>
              </div>
              <div
                className={`premium-follow-solution-container bg-white rounded-lg ${popup_padding} max-w-[600px] w-[90%] mx-auto lg:w-[auto] font-bold`}
              >
                <h4 className="text-[24px] font-bold text-darkgray poppins-font leading-[24px] border-b border-mist pb-[16px] mb-4">
                  Select a list
                </h4>
                <p className="text-[18px] font-normal  text-darkgray leading-[22px] karla-font mb-[16px]">
                  Keep track of your followed solutions by adding it to one of the following lists.
                </p>
                <ul className="flex flex-col items-center h-auto relative opacity-100 bg-white gap-[8px]">
                  {followTypes.map((followDetail, index) => (
                    <li
                      className={`flex w-full h-[86px] max-h-[100px] rounded-lg opacity-100 items-center gap-6 px-4 ${
                        followDetail.value === localFollowType
                          ? 'bg-[#f1f0fe] border border-[#3329ef]'
                          : 'bg-white border border-[#dddddd]'
                      }`}
                      key={`${followDetail.label}-${index}`}
                    >
                      <div className="w-5 h-[21px]  opacity-100 cursor-pointer">
                        <input
                          type="radio"
                          className="w-full h-full cursor-pointer"
                          name="followType"
                          value={followDetail.value}
                          onChange={(e) => updateUserFollowType(e.target.value)}
                          checked={localFollowType === followDetail.value}
                        />
                      </div>

                      <div className="flex flex-col ">
                        <span className="h-auto opacity-100 text-[#333333] mb-[7px] font-poppins font-[600] text-[18px] leading-[100%] poppins-font">
                          {followDetail.label}
                        </span>
                        <span className="h-auto opacity-100 text-[#333333] font-normal text-[16px] leading-[22px] karla-font">
                          {followDetail.description}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-[30px] flex w-full min-w-0 flex-row flex-wrap items-center md:gap-[16px] gap-[8px] font-sans">
                  <button
                    type="button"
                    onClick={handleFollowSolution}
                    disabled={isAddSolutionDisabled}
                    className={cn(
                      save_button,
                      isAddSolutionDisabled ? 'bg-[#b3bbcd] cursor-not-allowed' : 'bg-navy cursor-pointer',
                    )}
                  >
                    <p className="text-white">{isMoving ? 'Move Solution' : 'Add Solution'}</p>
                  </button>

                  <button className={`${cancel_button}`} type="button" onClick={handleClose}>
                    <p className="text-[#333333]">Cancel</p>
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        </>
      )}
    </>
  )
}
